import { camelCase, get, isEmpty, startCase, toLower } from "lodash";
import moment from "moment";
import Timecode from "./Timecode";
import {
  LANGUAGE_CODES,
  LANGUAGE_CODES_BCP47,
} from "../constants/languageCodes";
import { DISPLAY_FALLBACK } from "../constants/ContentFinder";

export const titleCaseWords = (value: string) =>
  value
    .split(" ")
    .map((word) => (word.length > 3 ? startCase(camelCase(word)) : word))
    .join(" ");

export const getIso639LanguageFromCode = (code: string) =>
  `${get(LANGUAGE_CODES, code, "")} (Unspecified)`;

export const getBcp47LanguageFromCode = (code: string) =>
  get(LANGUAGE_CODES_BCP47, code) ||
  get(LANGUAGE_CODES_BCP47, toLower(code)) ||
  DISPLAY_FALLBACK;

export const formatMillisecondsToDuration = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(totalHours % 24)}:${pad(totalMinutes % 60)}:${pad(
    totalSeconds % 60
  )}`;
};

export const displayData = (
  value,
  options: {
    titleCase?: boolean;
    removeUnderscores?: boolean;
    removeHyphens?: boolean;
    displayLanguage?: boolean;
    isBCP47?: boolean;
    dateFormat?: string;
    formatDuration?: boolean;
    decimalPlaces?: number;
    expression?: any;
    displayFallback?: string;
  } = {}
) => {
  let displayValue = value;
  const fallback = options.displayFallback || DISPLAY_FALLBACK;

  if ("expression" in options) {
    displayValue = options.expression ? displayValue : false;
  }

  if (
    typeof options.dateFormat === "string" &&
    (typeof displayValue === "string" || typeof displayValue === "number")
  ) {
    displayValue = moment(displayValue).format(options.dateFormat);
  }

  if (typeof displayValue === "string") {
    if (options.removeUnderscores) {
      displayValue = displayValue.replace(/_/g, " ");
    }
    if (options.removeHyphens) {
      displayValue = displayValue.replace(/-/g, " ");
    }
    if (options.titleCase) {
      displayValue = titleCaseWords(displayValue);
    }
    if (options.displayLanguage) {
      displayValue = options.isBCP47
        ? getBcp47LanguageFromCode(displayValue)
        : getIso639LanguageFromCode(displayValue);
    }
  }

  if (typeof displayValue === "number") {
    if (options.formatDuration) {
      displayValue = formatMillisecondsToDuration(displayValue);
    }
    if (typeof options.decimalPlaces === "number") {
      displayValue = displayValue.toFixed(options.decimalPlaces);
    }
  }

  return displayValue || fallback;
};

export const hasAnyValue = (values: any[]): boolean =>
  values.some((value) => value !== null && value !== undefined && value !== "");

export const getMeta = (metadataById, id) =>
  get(metadataById, [id, "stuff"], {});

export const getLinks = (linksById, id): any[] => get(linksById, [id], []);

export const getIdFromUri = (uri) => (uri ? uri.split(":")[2] || "" : "");

export const getSourceQualifiedMasterId = (linksById, id) => {
  const link = getLinks(linksById, id).find(
    (l) => l.uriType === "source-qualified-master"
  );
  return getIdFromUri(get(link, "uri"));
};

export const isPrimaryDomesticVersion = (linksById, id) => {
  const primaryLink = getLinks(linksById, id).find((l) => l.primary);
  return get(primaryLink, "data.type") === "PRIMARY_DOMESTIC";
};

export const getQualifiedMasterTracks = (
  metadataById,
  linksById,
  id
): any[] => {
  const qmId = getSourceQualifiedMasterId(linksById, id);
  return get(getMeta(metadataById, qmId), "technical.tracks", []);
};

export const getVideoTrackProperties = (meta, qmTracks) => {
  const trackList = get(meta, "technical.trackList", []);
  const versionTracks = qmTracks.filter((track) =>
    trackList.includes(get(track, "trackNum"))
  );
  const videoTrack = versionTracks.find(
    (track) => get(track, "type") === "VIDEO"
  );
  return get(videoTrack, "properties", {});
};

export const getPrimaryAudioTrack = (qmTracks) =>
  qmTracks.find(
    (track) =>
      get(track, "type") === "AUDIO" &&
      get(track, "properties.audioContentKind") === "PRIMARY"
  );

export const getVersionVideoTracks = (meta, qmTracks): any[] => {
  const trackList = get(meta, "technical.trackList", []);
  return qmTracks.filter(
    (track) =>
      trackList.includes(get(track, "trackNum")) &&
      get(track, "type") === "VIDEO"
  );
};

export const getVersionAudioTracks = (qmTracks): any[] =>
  qmTracks.filter((track) => get(track, "type") === "AUDIO");

export const getVersionTimedTextTracks = (qmTracks): any[] =>
  qmTracks.filter((track) => {
    const type = get(track, "type");
    return type === "TIMED_TEXT" || type === "TIMED-TEXT";
  });

export const getLegacyAudioTracks = (meta): any[] =>
  get(meta, "legacy.tracks", []).filter((track) =>
    Object.prototype.hasOwnProperty.call(track, "audioType")
  );

export const getLegacyNotes = (meta): any[] => get(meta, "legacy.note", []);

export const getVersionDateAdded = (meta) =>
  get(meta, "legacy.createdDateTime", get(meta, "createdAt"));

export const generateAspectRatioDescription = (properties) => {
  const contentAspectRatio = parseFloat(
    get(properties, "contentAspectRatio", 0)
  );
  const frameAspectRatio = parseFloat(get(properties, "aspectRatio", 0));
  let description = "Unknown";

  if (contentAspectRatio && frameAspectRatio) {
    let label;
    if (contentAspectRatio.toFixed(2) === frameAspectRatio.toFixed(2)) {
      label = "Full Frame";
    } else if (contentAspectRatio > frameAspectRatio) {
      label = "Letterbox";
    } else {
      label = "Pillarbox";
    }
    description = `${contentAspectRatio} - ${frameAspectRatio} (${label})`;
  }
  return description;
};

export const getVersionBreaks = (meta) => {
  const segments = get(meta, "temporal.segments", []);
  const breakSegments = segments.filter((segment) =>
    toLower(get(segment, "type", "")).includes("break")
  );
  if (isEmpty(breakSegments)) {
    const contentSegments = segments.filter((segment) => {
      const type = toLower(get(segment, "type", ""));
      return type.includes("content") || type.includes("logo");
    });
    return Math.max(contentSegments.length - 1, 0);
  }
  return Math.max(breakSegments.length, 0);
};

export const getVersionStatus = (meta) => {
  const status = toLower(get(meta, "administrative.status", "pending"));
  return startCase(status.replace("legacy", "").trim());
};

export const getVersionSlug = (meta, linksById, id) => {
  const administrative = get(meta, "administrative", {});
  let slug = get(administrative, "slug", DISPLAY_FALLBACK);

  if (isPrimaryDomesticVersion(linksById, id)) {
    slug = `${slug} (Primary Domestic Version)`;
  }
  if (get(meta, "importedFromBIM")) {
    slug = `${slug} (Legacy)`;
  }
  if (get(administrative, "simulated")) {
    slug = `${slug} [SIMULATED]`;
  }

  return slug;
};

export const getVersionCreatedAt = (meta) => {
  const administrative = get(meta, "administrative", {});
  if (administrative.status !== "active") return null;
  return get(administrative, "versionCreatedAt");
};

export const getVersionRating = (administrative) => {
  const rating = get(administrative, "versionRating.rating", "");
  const descriptors = get(administrative, "versionRating.descriptors", []).join(
    ""
  );
  return { rating, text: `${rating} ${descriptors}` };
};

export const buildColumnValues = (meta, qmTracks) => {
  const administrative = get(meta, "administrative", {});
  const videoProps = getVideoTrackProperties(meta, qmTracks);
  const primaryAudioTrack = getPrimaryAudioTrack(qmTracks);

  return {
    network: displayData(get(administrative, "networkName")),
    aspectRatio: generateAspectRatioDescription(videoProps),
    scanType: displayData(get(videoProps, "scanType"), { titleCase: true }),
    audio: displayData(
      get(primaryAudioTrack, "properties.soundFieldGroupType"),
      {
        removeUnderscores: true,
        titleCase: true,
      }
    ),
    language: displayData(
      get(primaryAudioTrack, "properties.audioContentLanguage"),
      { displayLanguage: true }
    ),
    breaks: getVersionBreaks(meta),
    status: getVersionStatus(meta),
  };
};

const createTimecode = (timecode: string, frameRate: number) =>
  new Timecode(timecode, frameRate || 24);

export const timecodeToFrames = (timecode: string, frameRate: number) => {
  if (!timecode) return 0;
  try {
    return createTimecode(timecode, frameRate).getFrameCount();
  } catch (error) {
    return 0;
  }
};

export const framesToTimecode = (frameNumber: number, frameRate: number) => {
  const frames = Math.max(0, Math.round(frameNumber));
  try {
    return new Timecode({ frameCount: frames }, frameRate || 24).toString();
  } catch (error) {
    return DISPLAY_FALLBACK;
  }
};

const getSegmentDurationTimecode = (segment, frameRate: number) => {
  const start = createTimecode(get(segment, "startTime"), frameRate);
  const stop = createTimecode(get(segment, "stopTime"), frameRate);
  stop.subtract(start);
  return stop;
};

export const getSegmentDurationFrames = (segment, frameRate: number) =>
  timecodeToFrames(get(segment, "stopTime"), frameRate) -
  timecodeToFrames(get(segment, "startTime"), frameRate);

export const getSegmentDurationString = (segment, frameRate: number) => {
  const startTime = get(segment, "startTime");
  const stopTime = get(segment, "stopTime");
  if (!startTime || !stopTime) return DISPLAY_FALLBACK;
  try {
    return getSegmentDurationTimecode(segment, frameRate).toString();
  } catch (error) {
    return DISPLAY_FALLBACK;
  }
};

export const getSegmentsTotalDuration = (segments, frameRate: number) => {
  const durationSegments = segments.filter((segment) =>
    /content|break/i.test(get(segment, "type", ""))
  );
  try {
    if (isEmpty(durationSegments)) {
      return new Timecode(0, frameRate || 24).toString();
    }
    return durationSegments
      .map((segment) => getSegmentDurationTimecode(segment, frameRate))
      .reduce((total, duration) => total.add(duration))
      .toString();
  } catch (error) {
    return DISPLAY_FALLBACK;
  }
};
